import React, { useEffect, useRef, useState } from 'react';
import { ChevronDown } from 'lucide-react';

/**
 * Compact multi-select chip picker. Empty selection is shown as "None"
 * (meaning: no filter applied / show all). Click outside closes the menu.
 * Used across Slip Tracker + Payments modals for client-type / salesperson
 * inclusion filters.
 */
const MultiPicker = ({ label, options, value, onChange, testid, accent = 'violet', width = 'w-40' }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onDoc = (e) => { if (open && ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const accentBtn = {
    violet: 'text-violet-600',
    emerald: 'text-emerald-600',
    sky: 'text-sky-600',
  }[accent] || 'text-violet-600';

  return (
    <div className={`relative ${width}`} ref={ref}>
      {label && <div className="text-slate-500 font-semibold mb-1 text-[10px] uppercase tracking-wide">{label}</div>}
      <button type="button" onClick={() => setOpen((o) => !o)}
        className="w-full border border-slate-200 rounded-md px-2 py-1 text-left flex items-center justify-between text-[11px] bg-white hover:bg-slate-50"
        data-testid={testid}>
        <span className="truncate">{value.length ? `${value.length} selected` : 'All'}</span>
        <ChevronDown className="w-3 h-3 shrink-0 text-slate-400" />
      </button>
      {open && (
        <div className="absolute z-50 mt-1 w-full max-h-56 overflow-auto bg-white border border-slate-200 rounded-md shadow-lg p-1">
          {options.length === 0 ? (
            <div className="text-slate-400 text-[10px] p-2">No options</div>
          ) : (
            options.map((o) => (
              <label key={o} className="flex items-center gap-1.5 py-0.5 px-1.5 cursor-pointer hover:bg-slate-50 rounded"
                data-testid={`${testid}-opt-${o}`}>
                <input type="checkbox" checked={value.includes(o)}
                  onChange={(e) => onChange(e.target.checked ? [...value, o] : value.filter((x) => x !== o))} />
                <span className="text-[10.5px]">{o}</span>
              </label>
            ))
          )}
          <div className="flex justify-between border-t border-slate-100 mt-1 pt-1 px-1">
            <button type="button" onClick={() => onChange([])} className="text-[10px] text-slate-500 hover:underline">Clear</button>
            <button type="button" onClick={() => setOpen(false)} className={`text-[10px] ${accentBtn} hover:underline`}>Done</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default MultiPicker;
